import { AuthProvider } from '@refinedev/core';
import { axiosInstance, adminApi } from '@/services/api';
import { AxiosError } from 'axios';

export interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  avatarUrl?: string;
  role: {
    id: string;
    name: string;
    type: string;
  };
  permissions: string[];
  lastLoginAt?: string;
}

export const authProvider: AuthProvider = {
  login: async ({ email, password }) => {
    try {
      const response = await axiosInstance.post('/admin/auth/login', {
        email,
        password,
      });

      const { accessToken, refreshToken, user } = response.data;

      localStorage.setItem('admin_token', accessToken);
      localStorage.setItem('admin_refresh_token', refreshToken);
      localStorage.setItem('admin_user', JSON.stringify(user));

      // Fetch read-only module config
      try {
        const readonlyRes = await adminApi.getReadonlyModules();
        localStorage.setItem('admin_readonly_modules', JSON.stringify(readonlyRes.data));
      } catch {
        // Non-critical — default to no read-only restrictions
        localStorage.setItem('admin_readonly_modules', '{}');
      }

      return {
        success: true,
        redirectTo: '/',
      };
    } catch (error) {
      const err = error as AxiosError<{ message: string }>;
      return {
        success: false,
        error: {
          name: 'LoginError',
          message: err.response?.data?.message || 'Invalid credentials',
        },
      };
    }
  },

  logout: async () => {
    try {
      await axiosInstance.post('/admin/auth/logout');
    } catch {
      // Ignore logout errors
    }

    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_refresh_token');
    localStorage.removeItem('admin_user');
    localStorage.removeItem('admin_readonly_modules');

    return {
      success: true,
      redirectTo: '/login',
    };
  },

  check: async () => {
    const token = localStorage.getItem('admin_token');

    if (token) {
      return {
        authenticated: true,
      };
    }

    return {
      authenticated: false,
      redirectTo: '/login',
    };
  },

  getIdentity: async () => {
    const userStr = localStorage.getItem('admin_user');

    if (userStr) {
      const user = JSON.parse(userStr) as AdminUser;
      return {
        id: user.id,
        name: user.fullName,
        avatar: user.avatarUrl,
        email: user.email,
        role: user.role.type,
      };
    }

    return null;
  },

  getPermissions: async () => {
    const userStr = localStorage.getItem('admin_user');

    if (userStr) {
      const user = JSON.parse(userStr) as AdminUser;
      return user.permissions;
    }

    return [];
  },

  onError: async (error) => {
    if (error.statusCode === 401) {
      return {
        logout: true,
        redirectTo: '/login',
      };
    }

    return { error };
  },
};
